<template>
  <FieldLabel v-if="label" :label="label" />

  <div class="image-picker d-flex flex-column flex-sm-row align-center">
    <!-- Dropzone nur zeigen, solange kein Bild vorhanden ist (Inhaber-Feedback
         III.4: mit Bild nur noch Vorschau + Ändern/Löschen anzeigen). -->
    <div
      v-if="!previewUrl"
      class="dropzone pa-6 text-center"
      @dragover.prevent="dragover = true"
      @dragleave.prevent="dragover = false"
      @drop.prevent="handleDrop"
      @click="openFileDialog"
      :class="{ 'dropzone--active': dragover }"
    >
      <UploadIcon class="upload-icon" />
      <div class="upload-subtitle">{{ uploadText }}</div>
      <div class="upload-formats">{{ $gt('Erlaubte Formate: JPG, PNG (max.') }} {{ maxFileSizeMB }} {{ $gt('MB)') }}</div>
    </div>
    <input ref="fileInput" type="file" accept=".jpg, .jpeg, .png" class="hidden-input" @change="handleFiles" />

    <div v-if="previewUrl" class="preview-container text-center">
      <div class="preview-wrapper">
        <img :src="previewUrl" :alt="$gt('Logo Vorschau')" class="preview-image" />
        <ButtonBase iconType="delete" iconStyle size="sm" color="error" class="delete-button" @click="removeImage" />
      </div>
      <ButtonBase buttonText="Bild ändern" iconType="edit" size="sm" class="mt-2" @click="openFileDialog" />
    </div>
  </div>

  <div v-if="uploadError" class="error-alert d-flex align-start mt-6">
    <AlertTriangle class="error-icon mt-1" />
    <div class="ml-3">
      <p class="text-error">
        {{ $gt('Die ausgewählte Datei konnte nicht ausreichend verkleinert werden (max.') }}
        <strong>{{ maxFileSizeMB }} {{ $gt('MB') }}</strong>). <br />{{ $gt('Bitte wählen Sie ein kleineres Bild.') }}
      </p>
    </div>
  </div>

  <div v-else-if="compressionInfo" class="info-alert d-flex align-start mt-6">
    <ImageDown class="info-icon mt-1" />
    <div class="ml-3">
      <p>{{ compressionInfo }}</p>
    </div>
  </div>
</template>

<script>
import FieldLabel from "@/components/inputs/FieldLabel.vue";
import ButtonBase from "@/components/buttons/ButtonBase.vue";

import { FileUp as UploadIcon, AlertTriangle, ImageDown } from "lucide-vue-next";
import { prepareImageUpload, imageUploadErrorText } from "@/utils/imageUpload";

export default {
  name: "ImagePicker",

  components: {
    ButtonBase,
    FieldLabel,
    UploadIcon,
    AlertTriangle,
    ImageDown,
  },

  props: {
    label: {
      type: String,
      default: "",
    },
    modelValue: {
      type: [File, Object, String, null],
      default: null,
    },
    maxFileSizeMB: {
      type: Number,
      default: 10,
    },
    uploadText: {
      type: String,
      default: "Bild hierher ziehen oder klicken",
    },
  },

  emits: ["update:modelValue"],

  data() {
    return {
      dragover: false,
      file: null,
      previewUrl: null,
      objectUrl: null,
      uploadError: false,
      // Issue #363: Hinweis, wenn ein zu grosses Bild automatisch komprimiert wurde.
      compressionInfo: "",
    };
  },

  watch: {
    modelValue: {
      immediate: true,
      handler(val) {
        if (val instanceof File) {
          this.setPreviewFromFile(val);
        } else if (typeof val === "string" && val !== "") {
          this.previewUrl = val;
        } else {
          this.clearPreview();
        }
      },
    },
  },

  beforeUnmount() {
    this.revokeObjectUrl();
  },

  methods: {
    openFileDialog() {
      this.$refs.fileInput.click();
    },

    handleFiles(e) {
      const file = e.target.files?.[0];
      if (file) this.loadFile(file);
      e.target.value = "";
    },

    handleDrop(e) {
      this.dragover = false;
      const file = e.dataTransfer.files?.[0];
      if (file) this.loadFile(file);
    },

    async loadFile(file) {
      // Typprüfung, Größenlimit und automatische Komprimierung (Issue #363)
      // liegen in utils/imageUpload – gemeinsam mit dem Profilbild-Picker.
      const result = await prepareImageUpload(file, this.maxFileSizeMB);

      if (!result.ok && result.reason === "type") {
        alert(imageUploadErrorText("type", this.maxFileSizeMB));
        return;
      }

      // Jeder neue Versuch setzt den vorherigen Zustand zurueck
      // (Issue #364: alte rote Fehlermeldung blieb sonst stehen).
      this.uploadError = false;
      this.compressionInfo = "";

      if (!result.ok) {
        this.uploadError = true;
        this.clearPreview();
        return;
      }

      this.compressionInfo = result.compressionInfo;
      this.file = result.file;
      this.setPreviewFromFile(result.file);
      this.$emit("update:modelValue", result.file);
    },

    setPreviewFromFile(file) {
      this.revokeObjectUrl();
      this.objectUrl = URL.createObjectURL(file);
      this.previewUrl = this.objectUrl;
      this.file = file;
    },

    revokeObjectUrl() {
      if (this.objectUrl) {
        URL.revokeObjectURL(this.objectUrl);
        this.objectUrl = null;
      }
    },

    clearPreview() {
      this.revokeObjectUrl();
      this.file = null;
      this.previewUrl = null;
    },

    removeImage() {
      this.clearPreview();
      this.uploadError = false;
      this.compressionInfo = "";
      this.$emit("update:modelValue", null);
    },
  },
};
</script>

<style scoped>
/* Container */
.image-picker {
  width: 100%;
  max-width: 750px;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  margin-top: 6px;
}

/* Left tile */
.dropzone {
  flex: 1 1 0;
  min-height: 160px;
  height: 160px;
  width: 100%;
  border: 1px dashed var(--grey-color);
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
}

.dropzone--active {
  background-color: var(--primary-light-color-transparent);
}

.hidden-input {
  display: none;
}

.upload-icon {
  width: 42px;
  height: 42px;
  stroke-width: 0.75;
  color: var(--primary-color);
}

.upload-subtitle {
  margin-top: 8px;
  font-size: 0.9rem;
  color: var(--primary-color);
}

.upload-formats {
  font-size: 0.8rem;
  color: var(--grey-color);
  margin-top: 4px;
}

/* Right tile */
.preview-container {
  width: 200px;
  min-width: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.preview-wrapper {
  position: relative;
  width: 100%;
  height: 160px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid var(--border-grey-color);
  display: flex;
  align-items: center;
  justify-content: center;
}

.delete-button {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
}

.preview-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.placeholder-icon {
  width: 48px;
  height: 48px;
  stroke-width: 0.75;
  color: var(--grey-color);
  opacity: 0.7;
}

/* Error alert */
.error-alert {
  border: 1px solid var(--error-color);
  background: var(--error-light-color-transparent);
  border-radius: 8px;
  padding: 12px 16px;
  color: var(--error-color);
}

.error-icon {
  width: 22px;
  height: 22px;
  stroke: var(--error-color);
  stroke-width: 1.5;
  flex-shrink: 0;
}

/* Info alert (Issue #363: automatische Komprimierung) */
.info-alert {
  border: 1px solid var(--primary-color);
  background: var(--primary-light-color-transparent);
  border-radius: 8px;
  padding: 12px 16px;
  color: var(--primary-color);
  max-width: 750px;
}

.info-icon {
  width: 22px;
  height: 22px;
  stroke: var(--primary-color);
  stroke-width: 1.5;
  flex-shrink: 0;
}
</style>
